import React, { useEffect, useMemo } from 'react'
import PropTypes from 'prop-types'
import { useDispatch, useSelector } from 'react-redux'
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts'
import { fetchSalesTrends } from '../../state/redux/actions/analyticsAction'

const TotalSales = ({ data: propData }) => {
  const dispatch = useDispatch()
  const { salesTrends, loading: analyticsLoading } = useSelector(s => s.analytics)

  useEffect(() => {
    if (
      (!propData || propData.length === 0) &&
      (!salesTrends || !Array.isArray(salesTrends.data) || salesTrends.data.length === 0)
    ) {
      dispatch(fetchSalesTrends())
    }
  }, [dispatch, propData, salesTrends])

  const chartData = useMemo(() => {
    // Prefer thunk-provided salesTrends data when available (payload may be { success, data: [...] })
    const sourceData = (salesTrends && Array.isArray(salesTrends.data) && salesTrends.data.length > 0)
      ? salesTrends.data
      : Array.isArray(propData) ? propData : []

    if (!Array.isArray(sourceData)) return []

    const timeSlots = ['7 AM', '8 AM', '9 AM', '10 AM', '11 AM', '12 PM', '1 PM', '2 PM', '3 PM', '4 PM', '5 PM']

    return sourceData.map((item, idx) => {
      // default label from _id (try parsing as date)
      const defaultLabel = (() => {
        if (!item || !item._id) return ''
        try {
          const dt = new Date(item._id)
          if (!isNaN(dt.getTime())) return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
        } catch (e) {
          // ignore
        }
        return String(item._id)
      })()

      // if _id looks like YYYY-MM-DD (or ISO), treat it as date for x-axis label
      const isDateLike = typeof item._id === 'string' && /^\d{4}-\d{2}-\d{2}/.test(item._id)
      const timeLabel = isDateLike ? defaultLabel : (idx >= 0 && idx < timeSlots.length ? timeSlots[idx] : defaultLabel)

      const sold = Number(item.sold || 0)
      const rented = Number(item.rented || 0)
      const total = Number(item.total || 0)

      const average = (sold + rented + total) / 3

      return {
        time: timeLabel,
        'Total sold': sold,
        'Total rented': rented,
        'Total sales': total,
        Average: parseFloat(average.toFixed(2)),
      }
    })
  }, [propData, salesTrends])

  if (process.env.NODE_ENV !== 'production') {
    // eslint-disable-next-line no-console
    console.debug('TotalSales chartData:', chartData)
  }

  // Dynamic Y-axis based on data
  const { maxValue, yAxisTicks } = useMemo(() => {
    if (!chartData || chartData.length === 0) return { maxValue: 10, yAxisTicks: [0, 2, 4, 6, 8, 10] }

    const calculatedMax = chartData.reduce(
      (m, r) => Math.max(m, r['Total sales'] || 0, r['Total rented'] || 0, r['Total sold'] || 0, r['Average'] || 0),
      0
    )

    // Calculate appropriate max value and ticks based on data range
    let maxValue = Math.max(calculatedMax, 1) // Ensure at least 1

    // If max value is small, use smaller increments
    if (maxValue <= 10) {
      maxValue = Math.ceil(maxValue) // Round up to nearest integer
      const step = Math.ceil(maxValue / 5) || 1
      const ticks = []
      for (let i = 0; i <= maxValue; i += step) {
        ticks.push(i)
      }
      return { maxValue, yAxisTicks: ticks }
    }

    // For larger values, use the original logic but make it flexible
    const roundedMax = Math.ceil(maxValue * 1.1) // Add 10% padding
    const niceMax = getNiceNumber(roundedMax)
    const step = niceMax / 5

    const ticks = []
    for (let i = 0; i <= niceMax; i += step) {
      ticks.push(i)
    }

    return { maxValue: niceMax, yAxisTicks: ticks }
  }, [chartData])

  // Helper function to get nice round numbers for axis
  const getNiceNumber = value => {
    const exponent = Math.floor(Math.log10(value))
    const fraction = value / Math.pow(10, exponent)

    let niceFraction
    if (fraction <= 1) niceFraction = 1
    else if (fraction <= 2) niceFraction = 2
    else if (fraction <= 5) niceFraction = 5
    else niceFraction = 10

    return niceFraction * Math.pow(10, exponent)
  }

  if (analyticsLoading) {
    return (
      <div className="flex items-center justify-center h-[350px]">
        <div className="text-lg text-gray-600">Loading sales trends...</div>
      </div>
    )
  }

  if (!chartData || chartData.length === 0) {
    return (
      <div className="flex items-center justify-center h-[350px]">
        <div className="text-gray-500">No sales trends data available</div>
      </div>
    )
  }

  return (
    <div className="w-full bg-white rounded-lg p-4 h-[400px] flex flex-col">
      {/* Legend positioned top right */}
      {/* Legend positioned top right */}
      <div className="flex justify-end mb-2">
        <div className="flex items-center space-x-4 text-[10px] ">
          {' '}
          {/* Updated: text-[10px] */}
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 rounded-full bg-[#471a81]"></div> {/* Circle icon */}
            <span>Total sales</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 rounded-full bg-[#5bc578]"></div> {/* Circle icon */}
            <span>Total rented</span>
          </div>
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 rounded-full bg-[#f18cdb]"></div> {/* Circle icon */}
            <span>Total sold</span>
          </div>
        </div>
      </div>

      <div className="flex-1 w-full h-full">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={chartData} margin={{ top: 20, right: 10, left: 0, bottom: 20 }}>
            <defs>
              <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#471a81" stopOpacity={0.0} />
                <stop offset="95%" stopColor="#471a81" stopOpacity={0.0} />
              </linearGradient>
              <linearGradient id="colorRented" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#5bc578" stopOpacity={0.0} />
                <stop offset="95%" stopColor="#5bc578" stopOpacity={0.0} />
              </linearGradient>
              <linearGradient id="colorSold" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#f18cdb" stopOpacity={0.0} />
                <stop offset="95%" stopColor="#f18cdb" stopOpacity={0.0} />
              </linearGradient>
              <linearGradient id="colorAverage" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#471a81" stopOpacity={0.1} />
                <stop offset="95%" stopColor="#471a81" stopOpacity={0.4} />
              </linearGradient>
            </defs>

            {/* <CartesianGrid 
              strokeDasharray="3 3" 
              vertical={false} 
              stroke="#f0f0f0" 
              horizontal={true}
            /> */}

            <XAxis
              dataKey="time"
              axisLine={{ stroke: '#000', strokeWidth: 1 }} // Solid black line for X-Axis
              tickLine={false}
              tick={{ fontSize: 12, fill: '#666' }}
              padding={{ left: 10, right: 10 }}
            />

            <YAxis
              tick={{ fontSize: 12, fill: '#666' }}
              tickFormatter={value => {
                if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`
                if (value >= 1000) return `${(value / 1000).toFixed(0)}k`
                return value
              }}
              domain={[0, maxValue]}
              ticks={yAxisTicks}
              axisLine={true} // Enable Y-axis line
              tickLine={true}
              tick={{ fontSize: 11, fill: '#666' }}
              width={45}
              interval={0}
            />

            <Tooltip
              contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
                fontSize: '12px',
                backgroundColor: 'white',
              }}
              labelStyle={{ fontWeight: 'bold', color: '#333', fontSize: '12px' }}
              itemStyle={{ color: '#555', fontSize: '12px' }}
              formatter={(value, name) => [typeof value === 'number' ? value.toLocaleString() : value, name]}
            />

            <Area
              type="linear"
              dataKey="Total sales"
              stroke="#471a81"
              fill="url(#colorSales)"
              strokeWidth={1}
              dot={false}
            />
            <Area
              type="linear"
              dataKey="Total rented"
              stroke="#5bc578"
              fill="url(#colorRented)"
              strokeWidth={1}
              dot={false}
            />
            <Area
              type="linear"
              dataKey="Total sold"
              stroke="#f18cdb"
              fill="url(#colorSold)"
              strokeWidth={1}
              dot={false}
            />
            {/* Average line - styled to match the design */}
            <Area
              type="monotone"
              dataKey="Average"
              stroke="#471a81"
              fill="url(#colorAverage)"
              strokeWidth={0}
              dot={false}
            />
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  )
}

TotalSales.propTypes = {
  data: PropTypes.array,
}

TotalSales.defaultProps = {
  data: undefined,
}

export default TotalSales
